import React, { Dispatch, SetStateAction } from 'react';
import { useWindowResize } from 'hooks/useWindowResize';
import Typography from '../Typography';
import classes from './index.module.scss';

/**
 * Props interface for the Pagination component
 */
interface Props {
	/** Whether to show the pagination controls */
	showPagination: boolean;
	/** Array of page numbers to display as buttons */
	pages: number[];
	/** Currently active page number */
	currentPage: number;
	/** Total number of pages available */
	totalPages: number;
	/** Function to go to the next page */
	incrementPage: () => void;
	/** Function to go to the previous page */
	decrementPage: () => void;
	/** Function to set the current page directly */
	setCurrentPage: Dispatch<SetStateAction<number>>;
	/** Function to navigate to a specific page */
	setPage: (page: number) => void;
}

/**
 * Pagination component
 * Renders a set of pagination controls for navigating through multi-page content
 * Includes First, Previous, Page Numbers, Next, and Last buttons
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const Pagination = ({
	showPagination,
	pages,
	currentPage,
	totalPages,
	incrementPage,
	decrementPage,
	setCurrentPage,
	setPage,
}: Props) => {
	// Responsive design helpers
	const { isSmallerDevice } = useWindowResize();

	return (
		<div style={{ display: !showPagination ? 'none' : 'flex' }} className={classes['bottom-toolbar']}>
			{/* First page button */}
			<button
				className={classes['badge']}
				onClick={() => setCurrentPage(1)}
				// Reduce opacity when already on first page
				style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
			>
				<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="darkgrey">
					First
				</Typography>
			</button>

			{/* Previous page button */}
			<button
				onClick={decrementPage}
				className={classes['badge']}
				// Reduce opacity when already on first page
				style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
			>
				<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="darkgrey">
					Prev.
				</Typography>
			</button>

			{/* Page number buttons */}
			{pages?.map((page) => {
				// Apply active styling to current page
				const classname =
					page === currentPage ? `${classes['badge']} ${classes['active']}` : `${classes['badge']}`;
				return (
					<button className={classname} key={page.toString()} onClick={() => setPage(page)}>
						<Typography
							variant={isSmallerDevice ? 'h5' : 'h6'}
							// Use primary color for current page, grey for others
							color={page === currentPage ? 'primary' : 'darkgrey'}
						>
							{page}
						</Typography>
					</button>
				);
			})}

			{/* Next page button */}
			<button
				onClick={incrementPage}
				className={classes['badge']}
				// Reduce opacity when already on last page
				style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
			>
				<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="darkgrey">
					Next
				</Typography>
			</button>

			{/* Last page button */}
			<button
				onClick={() => setCurrentPage(totalPages)}
				className={classes['badge']}
				// Reduce opacity when already on last page
				style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
			>
				<Typography variant={isSmallerDevice ? 'h5' : 'h6'} color="darkgrey">
					Last
				</Typography>
			</button>
		</div>
	);
};

export default Pagination;
